/**
 * @module RangeSlider
 */

import 'nouislider/distribute/nouislider.css';
import noUiSlider from 'nouislider';
import wNumb from 'wnumb';
import Component from '../Component';
import './nouislider.css';
import './styles.css';

/**
 * Слайдер диапазона.
 *
 * Использует плагин noUiSlider.
 * Для прямого доступа к функционалу плагина используется поле instance.
 *
 * Для форматирования применяется плагин wNumb.
 * Форматы определяются в статическом поле formats.
 *
 * Работа со значениями производится через определённые методы get/set.
 * Ряд свойств можно изменять динамически после инициализации - для этого предусмотрен метод update.
 *
 * @see noUiSlider: {@link https://refreshless.com/nouislider/}
 * @see wNumb: {@link https://refreshless.com/wnumb/}
 */
export default class RangeSlider extends Component {
    static className = 'range-slider';

    static formats = {
        integer: wNumb({
            decimals: 0,
        }),
        number: wNumb({
            decimals: 0,
            thousand: ' ',
        }),
        ruble: wNumb({
            decimals: 0,
            thousand: ' ',
            suffix: ' ₽',
        }),
    };

    initElements() {
        this.elements = {
            $control: $('.range-slider__control', this.$block),
            $values: $('.range-slider__value', this.$block),
            $inputs: $('.range-slider__input', this.$block),
        };
    }

    setTemplateProperties() {
        /**
         * Начальные значения слайдера.
         * @type {number[]}
         */
        this.start = this.$block
            .attr('data-start')
            .split(',')
            .map(Number);

        /**
         * Минимальное значение.
         * @type {number}
         */
        this.min = Number(this.$block.attr('data-min'));

        /**
         * Максимальное значение.
         * @type {number}
         */
        this.max = Number(this.$block.attr('data-max'));

        /**
         * Величина шага.
         * @type {number}
         */
        this.step = Number(this.$block.attr('data-step'));

        /**
         * Вертикальная ориентация.
         * @type {boolean}
         */
        this.vertical = this.$block.hasClass('range-slider--vertical');

        /**
         * Добавление тултипов.
         * @type {boolean}
         */
        this.tooltips = this.$block.hasClass('range-slider--tooltips');

        /**
         * Добавление шкалы.
         * @type {boolean}
         */
        this.pips = this.$block.hasClass('range-slider--pips');

        if (this.pips) {
            this.percent33 = this.$block
                .attr('data-percent33')
                .split(',')
                .map(item => +item.trim());
            this.percent66 = this.$block
                .attr('data-percent66')
                .split(',')
                .map(item => +item.trim());
        }
    }

    setProperties({ format, behaviour }) {
        /**
         * Формат выводимых значений.
         * @type {string}
         */
        this.format = format;

        /**
         * Нестандартное поведение.
         * @type {string}
         */
        this.behaviour = behaviour;
        /**
         * Настройки плагина.
         * @type {Object}
         */
        this.options = {
            start: this.start,
            range: {
                min: this.min,
                max: this.max,
            },
            connect: this.start.length === 1 ? [true, false] : true,
        };

        if (this.pips) {
            this.options.range = {
                min: this.min,
                '33%': this.percent33,
                '66%': this.percent66,
                max: this.max,
            };

            this.options.pips = {
                mode: 'range',
            };
        }

        if (this.step) {
            this.options.step = this.step;
        }

        if (this.vertical) {
            this.options.orientation = 'vertical';
        }

        if (behaviour) {
            this.options.behaviour = behaviour;
        }

        if (this.format) {
            this.options.format = RangeSlider.formats[this.format];
        }

        if (this.tooltips) {
            this.options.tooltips = new Array(this.start.length);
            this.options.tooltips.fill(this.format ? RangeSlider.formats[this.format] : true);
        }
    }

    initPlugin(pluginOptions) {
        Object.assign(this.options, pluginOptions);

        noUiSlider.create(this.elements.$control[0], this.options);

        /**
         * Объект для доступа к функционалу плагина.
         * @type {Object}
         */
        this.instance = this.elements.$control[0].noUiSlider;
    }

    init(options, pluginOptions) {
        super.init(options, pluginOptions);
        this.bindEvents();
    }

    /**
     * Вешает обработчики на события плагина.
     */
    bindEvents() {
        this.instance.on('update', (values, handle) => {
            if (this.elements.$values.length) {
                this.elements.$values.eq(handle).text(values[handle]);
            }

            if (this.elements.$inputs.length) {
                this.elements.$inputs.eq(handle).val(values[handle]);
            }
        });
    }

    /**
     * Возвращает текущее значение слайдера.
     *
     * @param {number} [index] Номер хэндла, указывается для множественного типа.
     * @param {boolean} [notFormatted = false] При передаче true возвращает неотформатированное значение (число).
     * @return {string|number} Текущее значение (строка в отформатированном виде, число в неотформатированном).
     */
    get(index, notFormatted) {
        let value = this.instance.get();

        if (Array.isArray(value) && index !== undefined) {
            value = value[index];
        }

        if (notFormatted) {
            if (this.format) {
                value = RangeSlider.formats[this.format].from(value);
            } else {
                value = Number(value);
            }
        }

        return value;
    }

    /**
     * Устанавливает новое значение слайдера.
     *
     * @param {number} value Новое значение.
     * @param {number} [index] Номер хэндла, указывается для множественного типа.
     */
    set(value, index) {
        const { length } = this.start;

        let newValue = value;

        let result;
        let nextValue;
        let otherIndex;
        let otherValue;
        let curValue;
        let diff;

        if (length === 1) {
            result = newValue;
        } else {
            result = new Array(length);
            result.fill(null);

            if (index !== length - 1) {
                nextValue = this.get(index + 1, true);
                newValue = newValue <= nextValue ? newValue : nextValue;
            }

            if (this.behaviour && this.behaviour.includes('fixed') && length === 2) {
                curValue = this.get(index, true);
                diff = newValue - curValue;
                otherIndex = index ? 0 : 1;

                otherValue = this.get(otherIndex, true);
                result[otherIndex] = otherValue + diff;
            }

            result[index] = newValue;
        }

        this.instance.set(result);
    }

    /**
     * Обновляет параметры, не требующие пересоздания:
     * https://refreshless.com/nouislider/more/#section-update
     *
     * @param {Object} options Новые параметры.
     */
    update(options) {
        /* Для step и range обновляются соответствующие свойства объекта */
        if (Object.prototype.hasOwnProperty.call(options, 'step')) {
            this.step = options.step;
        }

        if (Object.prototype.hasOwnProperty.call(options, 'range')) {
            this.min = options.range.min;
            this.max = options.range.max;
        }

        this.instance.updateOptions(options);
    }
}

$(document).on('change', '.range-slider__input', handleInputChange);

function handleInputChange(e) {
    const { currentTarget } = e;
    const $target = $(currentTarget);
    const $block = $target.closest('.range-slider');
    const rangeSlider = RangeSlider.getInstance($block);
    const { $inputs } = rangeSlider.elements;
    const isSingle = $inputs.length === 1;
    const value = $target.val();

    let inputIndex;

    if (isSingle) {
        rangeSlider.set(value);
    } else {
        inputIndex = $inputs.toArray().findIndex(input => $(input).is($target));
        rangeSlider.set(value, inputIndex);
    }
}

/* Шкала возраста ребенка */
$(window).on('load', () => {
    if ($('.range-slider--single-period').length !== 0) {
        setTimeout(() => {
            const $pips = $('.range-slider--single-period .noUi-pips .noUi-value');
            const timelineSlider = RangeSlider.getInstance($('.range-slider--single-period'));

            $pips.each((index, block) => {
                const $block = $(block);
                if ($block.data('value') == 0) {
                    $block.text('0-3 месяца');
                }
                if ($block.data('value') == 6) {
                    $block.text('3-6 месяцев');
                }
                if ($block.data('value') == 12) {
                    $block.text('6 месяцев - 1 год');
                }
                if ($block.data('value') == 36) {
                    $block.text('1-3 года');
                }
            });

            timelineSlider.instance.on('update', () => {
                const currentValue = timelineSlider.get();
                const $tooltipText = $('.range-slider--single-period .noUi-tooltip');

                if (currentValue == 0) {
                    $tooltipText.text('0-3 месяца');
                }
                if (currentValue == 6) {
                    $tooltipText.text('3-6 месяцев');
                }
                if (currentValue == 12) {
                    $tooltipText.text('6 месяцев - 1 год');
                }
                if (currentValue == 36) {
                    $tooltipText.text('1-3 года');
                }
            });
        }, 0);
    }
});
